import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { useFramework, useCombinedFrameworkConfig } from '@/modules/framework/api';
import {
  ReassessmentTriggerType,
  type ReassessmentStatus,
} from '@@/packages/shared/types/models/reassessment';
import {
  getConfirmedControlsWithAnswers,
  isApplicableControl,
  isControlConfirmed,
} from '@/selectors/controls-with-answers/controlsWithAnswers';
import { useControlsWithAnswers } from '../useControlsWithAnswers';
import { useScopingAnswers } from './assessment';
import { REASSESMENT_STATUS_QUERY } from './queryKeys';

export function useSubmitReassessment(options?: MutationOptions<void, void>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn() {
        await request('POST', `/reassessment/complete`);
      },
      async onSuccess() {
        // Refresh the status before the caller navigates: completing doesn't
        // otherwise update the query, and both the requireNotAssessing route
        // guard and the post-assessment modal read it. A stale
        // reassessment_inprogress / completed:false bounces the user off the
        // completion tab and suppresses the modal.
        await queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}

type ConfirmationResponse = {
  reassessmentStatus: ReassessmentStatus;
};

export function useReassessmentStatus(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [REASSESMENT_STATUS_QUERY],
      async queryFn() {
        const res = await request<ReassessmentStatus>('GET', '/reassessment/status');
        return res.data;
      },
    }),
  );

  const { sortedDomains } = useFramework();
  const { controlsWithAnswerForDomain, isLoading: isControlsWithAnswersLoading } =
    useControlsWithAnswers();
  const { frameworkRequiredControlIDs, isLoading: isCombinedFrameworkLoading } =
    useCombinedFrameworkConfig();

  const isLoading = computed(
    () =>
      query.isLoading.value ||
      isControlsWithAnswersLoading.value ||
      isCombinedFrameworkLoading.value,
  );

  const reassessmentStatus = computed(() => query.data.value);
  const answerConfirmations = computed(() => reassessmentStatus.value?.answerConfirmations ?? []);

  const getRequiredControlsWithAnswersForDomain = (domainID: string) =>
    controlsWithAnswerForDomain(domainID).filter((c) =>
      frameworkRequiredControlIDs.value.includes(c.controlID),
    );

  const { isDomainOutOfScope } = useScopingAnswers();

  const domainsWithConfirmationStatus = computed(() =>
    Object.fromEntries(
      sortedDomains.value.map((domain) => {
        const requiredControlsWithAnswers = getRequiredControlsWithAnswersForDomain(
          domain.domainID,
        );
        const confirmedControlsWithAnswers = getConfirmedControlsWithAnswers(
          requiredControlsWithAnswers,
          answerConfirmations.value,
        );

        return [
          domain.domainID,
          {
            domain,
            percentageConfirmed: Math.floor(
              (confirmedControlsWithAnswers.length / requiredControlsWithAnswers.length) * 100,
            ),
            confirmedControlsWithAnswers,
            isConfirmed: confirmedControlsWithAnswers.length === requiredControlsWithAnswers.length,
          },
        ];
      }),
    ),
  );

  function getNumberOfConfirmedApplicableAnswersInDomain(domainID: string) {
    const requiredControlsWithAnswers = getRequiredControlsWithAnswersForDomain(domainID);
    const confirmedApplicableAnswersInDomain = requiredControlsWithAnswers.filter(
      (controlWithAnswer) =>
        isApplicableControl(controlWithAnswer) &&
        isControlConfirmed(controlWithAnswer, answerConfirmations.value),
    );

    return confirmedApplicableAnswersInDomain.length;
  }

  function getPercentageDomainConfirmed(domainID: string) {
    return domainsWithConfirmationStatus.value[domainID]?.percentageConfirmed;
  }

  function getNumberOfConfirmedAnswersInDomain(domainID: string) {
    return domainsWithConfirmationStatus.value[domainID]?.confirmedControlsWithAnswers.filter(
      (controlWithAnswer) => isApplicableControl(controlWithAnswer),
    ).length;
  }

  function getApplicableControlsAndProductAnswersInDomain(domainID: string) {
    return getRequiredControlsWithAnswersForDomain(domainID).filter((controlWithAnswer) =>
      isApplicableControl(controlWithAnswer),
    );
  }

  const unconfirmedDomains = computed(() =>
    Object.entries(domainsWithConfirmationStatus.value)
      .filter(
        ([domainID, domainWithStatus]) =>
          !domainWithStatus.isConfirmed && !isDomainOutOfScope(domainID),
      )
      .map(([_domainID, domainWithStatus]) => domainWithStatus.domain),
  );

  const isAnswerConfirmed = (answerID: string) =>
    answerConfirmations.value.find((confirmation) => confirmation.answerID === answerID)?.confirmed;

  const isReassessing = computed(
    () => reassessmentStatus.value?.status === 'reassessment_inprogress',
  );

  const isReassessmentScheduled = computed(
    () => reassessmentStatus.value?.triggerType === ReassessmentTriggerType.ScheduledReassessment,
  );

  return {
    ...query,
    isLoading,
    getPercentageDomainConfirmed,
    getNumberOfConfirmedAnswersInDomain,
    getNumberOfConfirmedApplicableAnswersInDomain,
    getApplicableControlsAndProductAnswersInDomain,
    isAnswerConfirmed,
    reassessmentStatus,
    isReassessing,
    isReassessmentScheduled,
    domainsWithConfirmationStatus,
    unconfirmedDomains,
  };
}

export function useSetControlAnswerConfirmation(
  options?: MutationOptions<ConfirmationResponse, { answerID: string; isConfirmed: boolean }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ answerID, isConfirmed }) {
        const res = await request<ConfirmationResponse>(
          'POST',
          `/reassessment/answers/${answerID}/set-confirmation`,
          {
            confirmed: isConfirmed,
          },
        );
        return res.data;
      },
      async onSuccess(newData, variables) {
        // Optimistically update the control answer confirmation status
        queryClient.setQueryData<ReassessmentStatus>([REASSESMENT_STATUS_QUERY], (oldData) => {
          if (!oldData) return undefined;

          if (oldData.answerConfirmations.find((ac) => ac.answerID === variables.answerID)) {
            return {
              ...oldData,
              answerConfirmations: oldData.answerConfirmations.map((ac) =>
                ac.answerID === variables.answerID
                  ? { ...ac, confirmed: variables.isConfirmed }
                  : ac,
              ),
            };
          }

          return {
            ...oldData,
            answerConfirmations:
              newData.reassessmentStatus.answerConfirmations ?? oldData.answerConfirmations,
          };
        });
        // We need to get the full reassessment status
        await queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}

export function useConfirmDomain(
  options?: MutationOptions<ConfirmationResponse, { domainID: string }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ domainID }) {
        const res = await request<ConfirmationResponse>(
          'POST',
          `/reassessment/domain/${domainID}/confirm`,
        );
        return res.data;
      },
      async onSuccess(newData) {
        // Optimistically update the domain confirmation status
        queryClient.setQueryData<ReassessmentStatus>([REASSESMENT_STATUS_QUERY], (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            answerConfirmations:
              newData.reassessmentStatus.answerConfirmations ?? oldData.answerConfirmations,
          };
        });
        // We need to get the full reassessment status
        await queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}

export function useStartReassessment(options?: MutationOptions<ReassessmentStatus, void>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn() {
        const res = await request<ReassessmentStatus>('POST', `/reassessment/start`);
        return res.data;
      },
      async onSuccess(data) {
        queryClient.setQueryData<ReassessmentStatus>([REASSESMENT_STATUS_QUERY], data);

        await queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}
